import { useContext, useLayoutEffect, useState, useMemo, useRef, useCallback } from 'react'

import StoreContext from './StoreContext'


type FunctionSelector = (state: ReduxState) => any
type Selector = string | FunctionSelector
type PropsMap = FunctionSelector | { [key: string]: Selector }

const getIn = (obj, arrPath) => {
  const [ key, ...restPath ] = arrPath

  if (!restPath.length) {
    return obj[key]
  }

  const isOptional  = /\?$/.test(key)
  const realKey     = key.replace(/\?$/, '')
  const value       = obj[realKey]

  if (isOptional && (value === null || value === undefined)) {
    return undefined
  }

  return getIn(value, restPath)
}

const strPathToArr = (str) => str.split('.').filter((v) => v)

const lookup = (state: ReduxState, selector: Selector) => {
  if (typeof selector === 'function') {
    return selector(state)
  }

  if (typeof selector === 'string') {
    return getIn(state, strPathToArr(selector))
  }

  throw new Error(`Unknown lookup value: ${selector}`)
}

const mapStateToProps = (state: ReduxState, propsMap: PropsMap) => {
  if (typeof propsMap === 'function') {
    return propsMap(state)
  }

  return Object.keys(propsMap).reduce((res, propName) => ({
    ...res,
    [propName]: lookup(state, propsMap[propName]),
  }), {})
}


/**
 *
 * @param {Object} initialState
 * @param {Function|string} [selector]
 * @returns {function(*=)}
 */
const createListener = (initialState, selector: Selector) => {
  let prevValue = selector ? lookup(initialState, selector) : initialState

  return (newState) => {
    const newValue  = selector ? lookup(newState, selector) : newState
    const isEqual   = prevValue === newValue || JSON.stringify(prevValue) === JSON.stringify(newValue) // TODO replace this with memo

    prevValue = newValue

    return !isEqual
  }
}

/**
 *
 * @param initialState
 * @param {Object} propsMap
 * @returns {{root: Array}}
 */
const createListeners = (initialState: ReduxState, propsMap: PropsMap) => {
  const listeners: any = {}

  Object.keys(propsMap).forEach((propName) => {
    const propMap = propsMap[propName]

    // useConnect({ count: (state) => state.models.counter.count })
    if (typeof propMap === 'function') {
      if (!listeners.root) {
        listeners.root = []
      }

      listeners.root.push(createListener(initialState, propMap))
    }
    // useConnect({ count: 'models.counter.count' })
    else if (typeof propMap === 'string') {
      let [ rootName, reducerName, ...path ] = strPathToArr(propMap)

      rootName    = rootName.replace('?', '')
      reducerName = reducerName.replace('?', '')

      const listenerKey       = `${rootName}.${reducerName}` // models.counter
      const reducerState      = initialState[rootName][reducerName]
      const listenerPropsMap  = path.length ? path.join('.') : null
      const listener          = createListener(reducerState, listenerPropsMap)

      if (!listeners[listenerKey]) {
        listeners[listenerKey] = []
      }

      listeners[listenerKey].push(listener)
    }
    else {
      throw new Error(`Invalid prop "${propName}" of type "${typeof propMap}" supplied to "useConnect", expected "string" or "function".`)
    }
  })

  return listeners
}

// converts propsMap to keys array e.g. ['root', 'models.me']
const getListenersKeys = (propsMap: PropsMap): string[] => {
  const keysToListen: { [key: string]: boolean } = {}

  if (typeof propsMap === 'function') {
    keysToListen.root = true
  }
  else {
    Object.keys(propsMap).forEach((propName) => {
      const propMap = propsMap[propName]

      if (typeof propMap === 'function') {
        keysToListen.root = true
      }
      else {
        const [ rootName, reducerName ] = strPathToArr(propMap)
        const listenerKey = `${rootName}.${reducerName}`.replace('?', '') // models.counter

        keysToListen[listenerKey] = true
      }
    })
  }

  return Object.keys(keysToListen)
}


type Handler = {
  key: string
  handler: (state: any) => void
}

const useConnect = <Result>(propsMap: PropsMap, deps = []): Result => {
  const { store, storeEvents } = useContext(StoreContext)
  const [ renderIndex, setRenderIndex ] = useState(0)

  const forceUpdate = useCallback(() => {
    setRenderIndex((state) => {
      return state + 1
    })
  }, [])

  // ATTN state can be updated between initial render and useLayoutEffect
  //  it will cause loosing of state update, so we subscribe to state updates (with very lightweight handler)
  //  on first render to check if state were updated
  const keysToListen = useRef<string[]>([])
  const beforeLayoutEffectListener = useRef(null)
  const isStateWasChanged = useRef(false)

  if (!beforeLayoutEffectListener.current) {
    beforeLayoutEffectListener.current = () => {
      isStateWasChanged.current = true
    }

    keysToListen.current = getListenersKeys(propsMap)
    keysToListen.current.forEach((key) => {
      storeEvents.subscribe(key, beforeLayoutEffectListener.current)
    })
  }

  // ATTN "useLayoutEffect" is important here! The problem was that useConnect was subscribing to storeEvents
  //  in mount process and if redirect called in useWillMount when useConnect handlers called which calls
  //  unexpected rerender (of component where redirect called)
  useLayoutEffect(() => {
    const handlers: Handler[] = []

    let listeners: any = {}

    if (typeof propsMap === 'function') {
      listeners.root = [ createListener(store.getState(), propsMap) ]
    }
    else {
      listeners = createListeners(store.getState(), propsMap)
    }

    Object.keys(listeners).forEach((listenerKey) => {
      const handler = (newState: ReduxState) => {
        let isStateChanged = false

        listeners[listenerKey].forEach((listener) => {
          const isChanged = listener(newState)

          if (isChanged) {
            isStateChanged = true
          }
        })

        if (isStateChanged) {
          forceUpdate()
        }
      }

      handlers.push({ key: listenerKey, handler })
      storeEvents.subscribe(listenerKey, handler)
    })

    // ATTN if state was updated between initial render and useLayoutEffect,
    //  we should forceUpdate the component. We don't need to update listeners,
    //  because they have the latest value already
    if (beforeLayoutEffectListener.current) {
      keysToListen.current.forEach((key) => {
        storeEvents.unsubscribe(key, beforeLayoutEffectListener.current)
      })
      beforeLayoutEffectListener.current = null

      if (isStateWasChanged.current) {
        forceUpdate()
        isStateWasChanged.current = false
      }
    }

    return () => {
      handlers.forEach(({ key, handler }) => {
        storeEvents.unsubscribe(key, handler)
      })
    }
  }, [ ...deps ]) // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => (
    mapStateToProps(store.getState(), propsMap)
  ), [ renderIndex, ...deps ]) // eslint-disable-line react-hooks/exhaustive-deps
}


export default useConnect

export {
  createListeners,
}
